import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getApi, postApi, putApi, deleteApi } from "common/services/api";
import { API_PATHS } from "common/services/apiPaths";
import { toast } from "react-toastify";

// Get accepted offer applications for dropdown
export const getAcceptedOfferApplications = createAsyncThunk(
  "onboardingChecklist/getAcceptedOfferApplications",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getApi(API_PATHS.ONBOARDING_CHECKLIST_ACCEPTED_OFFERS);
      return response.data.data;
    } catch (error) {
      toast.error("Failed to fetch accepted offer applications");
      return rejectWithValue(error.message);
    }
  }
);

// Get all onboarding checklists
export const getOnboardingChecklists = createAsyncThunk(
  "onboardingChecklist/getOnboardingChecklists",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = queryString 
        ? `${API_PATHS.ONBOARDING_CHECKLIST}?${queryString}` 
        : API_PATHS.ONBOARDING_CHECKLIST;
      const response = await getApi(url);
      return response.data;
    } catch (error) {
      toast.error("Failed to fetch onboarding checklists");
      return rejectWithValue(error.message);
    }
  }
);

// Get single onboarding checklist
export const getOnboardingChecklist = createAsyncThunk(
  "onboardingChecklist/getOnboardingChecklist",
  async (id, { rejectWithValue }) => {
    try {
      const response = await getApi(`${API_PATHS.ONBOARDING_CHECKLIST}/${id}`);
      return response.data;
    } catch (error) {
      toast.error("Failed to fetch onboarding checklist");
      return rejectWithValue(error.message);
    }
  }
);

// Create onboarding checklist
export const createOnboardingChecklist = createAsyncThunk(
  "onboardingChecklist/createOnboardingChecklist",
  async ({ values, resetForm }, { dispatch, rejectWithValue }) => {
    try {
      const response = await postApi(API_PATHS.ONBOARDING_CHECKLIST, values);
      toast.success("Onboarding checklist created successfully");
      
      // Refresh the list
      await dispatch(getOnboardingChecklists());
      
      if (resetForm) resetForm();
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create onboarding checklist");
      return rejectWithValue(error.message);
    }
  }
);

// Update onboarding checklist
export const updateOnboardingChecklist = createAsyncThunk(
  "onboardingChecklist/updateOnboardingChecklist",
  async ({ id, values }, { dispatch, rejectWithValue }) => {
    try {
      const response = await putApi(`${API_PATHS.ONBOARDING_CHECKLIST}/${id}`, values);
      toast.success("Onboarding checklist updated successfully");
      
      // Refresh the list
      await dispatch(getOnboardingChecklists());
      
      return response.data;
    } catch (error) {
      toast.error("Failed to update onboarding checklist");
      return rejectWithValue(error.message);
    }
  }
);

// Update single task in checklist
export const updateChecklistTask = createAsyncThunk(
  "onboardingChecklist/updateChecklistTask",
  async ({ checklistId, taskId, completed, notes }, { rejectWithValue }) => {
    try {
      const response = await putApi(
        `${API_PATHS.ONBOARDING_CHECKLIST}/${checklistId}/task/${taskId}`,
        { completed, notes }
      );
      
      // Return the updated checklist from response
      return { checklistId, checklist: response.data.checklist };
    } catch (error) {
      toast.error("Failed to update task");
      return rejectWithValue(error.message);
    }
  }
);

// Delete onboarding checklist
export const deleteOnboardingChecklist = createAsyncThunk(
  "onboardingChecklist/deleteOnboardingChecklist",
  async (id, { dispatch, rejectWithValue }) => {
    try {
      await deleteApi(`${API_PATHS.ONBOARDING_CHECKLIST}/`, id);
      toast.success("Onboarding checklist deleted successfully");
      
      // Refresh the list
      await dispatch(getOnboardingChecklists());
      
      return id;
    } catch (error) {
      toast.error("Failed to delete onboarding checklist");
      return rejectWithValue(error.message);
    }
  }
);

const onboardingChecklistSlice = createSlice({
  name: "onboardingChecklist",
  initialState: {
    data: [],
    acceptedOffers: [],
    currentChecklist: null,
    metadata: {
      totalRecords: 0,
      totalPages: 0,
      page: 1,
      limit: 10
    },
    loading: false,
    acceptedOffersLoading: false,
    createLoading: false,
    updateLoading: false,
    deleteLoading: false,
    error: null,
  },
  reducers: {
    clearCurrentChecklist: (state) => {
      state.currentChecklist = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    optimisticTaskUpdate: (state, action) => {
      console.log("Optimistic task update action payload:",state, action);
      const { checklistId, updater } = action.payload;
      if (state.currentChecklist && state.currentChecklist._id === checklistId) {
        state.currentChecklist = updater(state.currentChecklist);
      }
      // Also update in the list
      const index = state.data.findIndex(c => c._id === checklistId);
      if (index !== -1) {
        state.data[index] = updater(state.data[index]);
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Get accepted offer applications
      .addCase(getAcceptedOfferApplications.pending, (state) => {
        state.acceptedOffersLoading = true;
      })
      .addCase(getAcceptedOfferApplications.fulfilled, (state, action) => {
        state.acceptedOffersLoading = false;
        state.acceptedOffers = action.payload || [];
      })
      .addCase(getAcceptedOfferApplications.rejected, (state, action) => {
        state.acceptedOffersLoading = false;
        state.error = action.payload;
      })
      
      // Get all checklists
      .addCase(getOnboardingChecklists.pending, (state) => {
        state.loading = true;
      })
      .addCase(getOnboardingChecklists.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data || [];
        state.metadata = action.payload.metadata || state.metadata;
      })
      .addCase(getOnboardingChecklists.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Get single checklist - don't show loading if we already have data
      .addCase(getOnboardingChecklist.pending, (state) => {
        if (!state.currentChecklist) {
          state.loading = true;
        }
      })
      .addCase(getOnboardingChecklist.fulfilled, (state, action) => {
        state.loading = false;
        state.currentChecklist = action.payload;
      })
      .addCase(getOnboardingChecklist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Create checklist
      .addCase(createOnboardingChecklist.pending, (state) => {
        state.createLoading = true;
      })
      .addCase(createOnboardingChecklist.fulfilled, (state, action) => {
        state.createLoading = false;
      })
      .addCase(createOnboardingChecklist.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
      })
      
      // Update checklist
      .addCase(updateOnboardingChecklist.pending, (state) => {
        state.updateLoading = true;
      })
      .addCase(updateOnboardingChecklist.fulfilled, (state, action) => {
        state.updateLoading = false;
      })
      .addCase(updateOnboardingChecklist.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
      })
      
      // Update task - use silent update without triggering re-renders
      .addCase(updateChecklistTask.fulfilled, (state, action) => {
        const { checklistId, checklist } = action.payload;

        if (state.currentChecklist && state.currentChecklist._id === checklistId) {
          // Find and update only the changed task
          const updatedTask = checklist.tasks.find(t => {
            const currentTask = state.currentChecklist.tasks.find(ct => ct._id === t._id);
            return currentTask && JSON.stringify(currentTask) !== JSON.stringify(t);
          });

          if (updatedTask) {
            const taskIndex = state.currentChecklist.tasks.findIndex(t => t._id === updatedTask._id);
            if (taskIndex !== -1) {
              state.currentChecklist.tasks[taskIndex] = updatedTask;
            }
          }

          // Update progress fields
          state.currentChecklist.progressPercentage = checklist.progressPercentage;
          state.currentChecklist.completedTasks = checklist.completedTasks;
          state.currentChecklist.totalTasks = checklist.totalTasks;
          state.currentChecklist.status = checklist.status;
        }

        // Update in the list
        const index = state.data.findIndex(c => c._id === checklistId);
        if (index !== -1) {
          state.data[index].progressPercentage = checklist.progressPercentage;
          state.data[index].completedTasks = checklist.completedTasks;
          state.data[index].totalTasks = checklist.totalTasks;
          state.data[index].status = checklist.status;
        }
      })
      .addCase(updateChecklistTask.rejected, (state, action) => {
        state.error = action.payload;
      })
      
      // Delete checklist
      .addCase(deleteOnboardingChecklist.pending, (state) => {
        state.deleteLoading = true;
      })
      .addCase(deleteOnboardingChecklist.fulfilled, (state, action) => {
        state.deleteLoading = false;
      })
      .addCase(deleteOnboardingChecklist.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearCurrentChecklist, clearError, optimisticTaskUpdate } = onboardingChecklistSlice.actions;
export default onboardingChecklistSlice.reducer;



